import { useEffect, useState, useMemo } from "react";
import { Loader2, RefreshCw, Mail, Phone, Briefcase, Inbox, FileText, Calendar } from "lucide-react";
import { toast } from "sonner";
import {
  DndContext, closestCorners, PointerSensor, useSensor, useSensors, DragOverlay,
} from "@dnd-kit/core";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import AdminShell from "@/components/admin/AdminShell";
import { adminApi } from "@/lib/adminApi";

const COLUMNS = [
  { key: "new", label: "New", color: "bg-[#2BB6E5]" },
  { key: "contacted", label: "Contacted", color: "bg-[#FFB836]" },
  { key: "closed", label: "Closed", color: "bg-[#475569]" },
];

const TABS = [
  { key: "quote-requests", label: "Quote Requests", icon: FileText },
  { key: "job-applications", label: "Job Applications", icon: Briefcase },
  { key: "contact-messages", label: "Contact Messages", icon: Inbox },
];

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-IE", { day: "numeric", month: "short" }); }
  catch { return ""; }
}

function getTitle(row, kind) {
  if (kind === "quote-requests") return row.company_name || row.contact_person;
  if (kind === "job-applications") return row.full_name;
  return row.name;
}
function getSubtitle(row, kind) {
  if (kind === "quote-requests") return row.contact_person;
  if (kind === "job-applications") return row.role;
  return row.subject;
}

function LeadCard({ row, kind, isOverlay = false }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: row.id });
  const title = getTitle(row, kind);
  const subtitle = getSubtitle(row, kind);
  return (
    <div
      ref={isOverlay ? null : setNodeRef}
      {...(isOverlay ? {} : attributes)}
      {...(isOverlay ? {} : listeners)}
      data-testid={`lead-card-${row.id}`}
      className={`bg-white border border-ags-hairline hover:border-[#0E2A57]/40 p-4 cursor-grab active:cursor-grabbing transition-all ${isDragging && !isOverlay ? "opacity-30" : ""} ${isOverlay ? "shadow-2xl rotate-2" : "hover:shadow-md"}`}
    >
      <div className="font-heading font-bold text-sm text-ags-ink tracking-tight truncate">{title || "Untitled"}</div>
      {subtitle && <div className="mt-0.5 text-xs text-ags-text-muted truncate">{subtitle}</div>}
      <div className="mt-3 flex flex-wrap gap-2 text-xs text-ags-text-muted">
        {row.email && <span className="inline-flex items-center gap-1"><Mail size={10} /> <span className="truncate max-w-[140px]">{row.email}</span></span>}
        {row.phone && <span className="inline-flex items-center gap-1"><Phone size={10} /> {row.phone}</span>}
      </div>
      <div className="mt-3 pt-3 border-t border-ags-hairline flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.18em] text-[#0E2A57]/55">
        <span><Calendar size={9} className="inline mr-1" />{fmtDate(row.created_at)}</span>
        {row.site_type && <span className="bg-ags-cream px-1.5 py-0.5">{row.site_type}</span>}
      </div>
    </div>
  );
}

function Column({ status, label, color, rows, kind, count }) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <div
      ref={setNodeRef}
      data-testid={`column-${status}`}
      className={`bg-ags-cream border border-ags-hairline flex flex-col min-h-[400px] transition-colors ${isOver ? "border-[#0E2A57] bg-[#BEE7F5]/30" : ""}`}
    >
      <div className="px-4 py-3 border-b border-ags-hairline flex items-center justify-between bg-white">
        <div className="flex items-center gap-2.5">
          <span className={`w-2 h-2 rounded-full ${color}`} />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ags-ink font-semibold">{label}</span>
        </div>
        <span className="font-mono text-[10px] text-ags-text-muted bg-ags-cream px-2 py-0.5">{count}</span>
      </div>
      <div className="p-3 space-y-2 flex-1 overflow-y-auto">
        {rows.length === 0 ? (
          <div className="text-xs text-ags-text-muted text-center py-6 italic">No leads here</div>
        ) : rows.map((r) => <LeadCard key={r.id} row={r} kind={kind} />)}
      </div>
    </div>
  );
}

export default function AdminPipeline() {
  const [activeTab, setActiveTab] = useState("quote-requests");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDrag, setActiveDrag] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/${activeTab}`);
      setRows(r.data || []);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [activeTab]);

  const byStatus = useMemo(() => {
    const out = { new: [], contacted: [], closed: [] };
    for (const r of rows) {
      const s = r.status || "new";
      (out[s] || out.new).push(r);
    }
    return out;
  }, [rows]);

  const onDragStart = (event) => setActiveDrag(rows.find((r) => r.id === event.active.id));
  const onDragEnd = async (event) => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over || !active) return;
    const lead = rows.find((r) => r.id === active.id);
    if (!lead) return;
    const newStatus = over.id;
    if (!COLUMNS.find((c) => c.key === newStatus)) return;
    if ((lead.status || "new") === newStatus) return;

    // optimistic
    setRows((prev) => prev.map((r) => r.id === lead.id ? { ...r, status: newStatus } : r));
    try {
      await adminApi.patch(`/admin/${activeTab}/${lead.id}/status`, { status: newStatus });
      toast.success(`Moved to ${newStatus}`);
    } catch (err) {
      toast.error("Failed to update status");
      load(); // refresh from server
    }
  };

  return (
    <AdminShell title="Lead Pipeline">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2 bg-white border border-ags-hairline p-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                data-testid={`pipeline-tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors ${
                  active ? "bg-[#0E2A57] text-white" : "text-ags-text-muted hover:text-[#0E2A57]"
                }`}
              >
                <Icon size={12} /> {t.label}
              </button>
            );
          })}
        </div>
        <button
          onClick={load}
          data-testid="pipeline-refresh"
          className="inline-flex items-center gap-2 border border-[#0E2A57]/30 text-[#0E2A57] hover:bg-[#0E2A57] hover:text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em]"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <p className="text-sm text-ags-text-muted mb-6">
        Drag a lead card between columns to update its status. Changes are saved automatically.
      </p>

      {loading ? (
        <div className="bg-white border border-[#0E2A57]/15 p-12 flex items-center justify-center text-ags-text-muted">
          <Loader2 size={20} className="animate-spin mr-3" /> Loading…
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {COLUMNS.map((c) => (
              <Column
                key={c.key}
                status={c.key}
                label={c.label}
                color={c.color}
                rows={byStatus[c.key] || []}
                kind={activeTab}
                count={(byStatus[c.key] || []).length}
              />
            ))}
          </div>
          <DragOverlay>
            {activeDrag ? <LeadCard row={activeDrag} kind={activeTab} isOverlay /> : null}
          </DragOverlay>
        </DndContext>
      )}
    </AdminShell>
  );
}
